import {
  EnhancedFieldConfig,
  FormatterValue,
  ValidationRule,
  ValidationResult,
  FormSchemaConfig,
  CalculationRule,
  EnhancedGroupedSection,
  EnhancedSection,
  FieldDependency,
  DependencyCondition,
} from '../types/form/schema.types';
import { FormDataType } from '@tw/tw-component-library';
import { SectionKey } from '../../../../types';
import { FormState } from '../types/core/form-state.types';

export const validateField = (
  fieldName: string,
  value: any,
  rules: ValidationRule[] | undefined,
  formData: FormDataType
): ValidationResult => {
  // Return early if no rules
  if (!Array.isArray(rules) || rules.length === 0) {
    return {
      isValid: true,
      errors: [],
    };
  }

  const errors = [];

  for (const rule of rules) {
    // Skip if rule is invalid
    if (!rule || typeof rule !== 'object') continue;

    let isValid = true;

    switch (rule.type) {
      case 'required':
        isValid = value !== undefined && value !== null && value !== '';
        break;
      case 'min':
        isValid = typeof value === 'number' && value >= (rule.value as number);
        break;
      case 'max':
        isValid = typeof value === 'number' && value <= (rule.value as number);
        break;
      case 'pattern':
        isValid =
          rule.value instanceof RegExp
            ? rule.value.test(String(value))
            : new RegExp(rule.value as string).test(String(value));
        break;
      case 'email':
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value));
        break;
      case 'custom':
        if (typeof rule.customValidator === 'function') {
          try {
            const result = rule.customValidator(value, formData);
            isValid = typeof result === 'boolean' ? result : false;
          } catch (error) {
            console.error('Custom validator error:', error);
            isValid = false;
          }
        }
        break;
      default:
        isValid = true;
        break;
    }

    if (!isValid) {
      errors.push({
        field: fieldName,
        message: rule.message || `Invalid ${fieldName}`,
        type: rule.type,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const processField = (
  field: EnhancedFieldConfig,
  formData: FormDataType,
  schemaConfig: FormSchemaConfig,
  sectionKey: SectionKey,
  completedSections: SectionKey[],
  formState?: FormState
): EnhancedFieldConfig => {
  const processedField: EnhancedFieldConfig = {
    ...field,
    validation: field.validation || [],
    dependencies: field.dependencies || [],
    calculations: field.calculations,
    value: field.defaultValue ?? formData[field.name],
    // Merging errorMessages / formatters if needed:
    errorMessages: { ...field.errorMessages },
    formatters: field.formatters,
  };

  // If the form is complete, disable all fields
  if (formState?.isComplete) {
    processedField.disabled = true;
    processedField.value = formData[field.name];
  }
  // If this section is marked complete, disable fields
  else if (completedSections.includes(sectionKey)) {
    processedField.disabled = true;
    processedField.value = formData[field.name];
  }

  // Process each dependency in a "modern" way
  if (processedField.dependencies) {
    processedField.dependencies.forEach((dependency) => {
      // Distinguish modern vs. legacy by checking if "conditions" is an array
      if (Array.isArray(dependency.conditions)) {
        const {
          type,
          conditions,
          logicalOperator = 'AND',
        } = dependency as FieldDependency;
        const conditionResults: boolean[] = [];

        // Evaluate each condition
        conditions.forEach((cond: DependencyCondition) => {
          const dependentValue = formData[cond.field];
          let singleResult = false;

          switch (cond.operator) {
            case 'equals':
              singleResult = dependentValue === cond.value;
              break;
            case 'notEquals':
              singleResult = dependentValue !== cond.value;
              break;
            case 'contains':
              singleResult =
                Array.isArray(dependentValue) &&
                dependentValue.includes(cond.value);
              break;
            case 'notContains':
              singleResult =
                Array.isArray(dependentValue) &&
                !dependentValue.includes(cond.value);
              break;
            case 'greaterThan':
              singleResult = Number(dependentValue) > Number(cond.value);
              break;
            case 'lessThan':
              singleResult = Number(dependentValue) < Number(cond.value);
              break;
            case 'isEmpty':
              singleResult =
                dependentValue === undefined ||
                dependentValue === null ||
                dependentValue === '' ||
                (Array.isArray(dependentValue) && dependentValue.length === 0);
              break;
            case 'isNotEmpty':
              singleResult =
                dependentValue !== undefined &&
                dependentValue !== null &&
                dependentValue !== '' &&
                (!Array.isArray(dependentValue) || dependentValue.length > 0);
              break;
            // Add other operators as needed
            default:
              singleResult = false;
          }
          conditionResults.push(singleResult);
        });

        // Combine results using logicalOperator
        const shouldApply =
          logicalOperator === 'AND'
            ? conditionResults.every(Boolean)
            : conditionResults.some(Boolean);

        // Apply changes to the field if conditions are met
        if (shouldApply) {
          switch (type) {
            case 'enable':
              processedField.disabled = false;
              break;
            case 'disable':
              processedField.disabled = true;
              break;
            case 'show':
              processedField.hidden = false;
              break;
            case 'hide':
              processedField.hidden = true;
              break;
            case 'require':
              processedField.validation = processedField.validation || [];
              processedField.validation.push({
                type: 'required',
                message: dependency.message || 'This field is required',
              });
              break;
          }
        }
      } else {
        // (Optional) If you still want to handle legacy single-condition dependencies:
        // ... legacy logic goes here ...
      }
    });
  }

  return processedField;
};

// Add this function to handle calculations
export const processCalculatedFields = (
  sections: (EnhancedSection | EnhancedGroupedSection)[],
  formData: FormDataType,
  calculations: Record<string, CalculationRule>
): Record<string, any> => {
  const updates: Record<string, any> = {};

  const processCalculation = (
    calculation: CalculationRule,
    fieldName: string
  ) => {
    const values = calculation.sourceFields.map((field) => formData[field]);

    if (calculation.customCalculation) {
      updates[fieldName] = calculation.customCalculation(values, formData);
      return;
    }

    let result: number;
    switch (calculation.type) {
      case 'sum':
        result = values.reduce((sum, val) => sum + (Number(val) || 0), 0);
        break;
      case 'multiply':
        result = values.reduce(
          (product, val) => product * (Number(val) || 1),
          1
        );
        break;
      case 'subtract':
        result = values.reduce((diff, val, idx) =>
          idx === 0 ? Number(val) : diff - (Number(val) || 0)
        );
        break;
      case 'divide':
        result = values.reduce((quotient, val, idx) =>
          idx === 0 ? Number(val) : quotient / (Number(val) || 1)
        );
        break;
      default:
        return;
    }

    if (calculation.roundTo !== undefined) {
      result = Number(result.toFixed(calculation.roundTo));
    }

    updates[fieldName] = result;
  };

  Object.entries(calculations).forEach(([fieldName, calculation]) => {
    processCalculation(calculation, fieldName);
  });

  return updates;
};
